import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { connect } from "pwa-helpers";
import paymentStyles from "../../../common/styles/payment-styles";
import { BaseComponent } from "../../../features/base-component/base-component";
import store from "../../../store/store";
import { StoreState } from "../../../store/store.interface";
import { createVenmoInstance, getVenmoInstance, getVenmoInstanceStatus } from "../braintree";
import { ActionStatus } from "../../../common/constants/action-status";
import { waitForElementsToLoad } from "../../../common/utils/delay-util";
import emit from "../../../common/events";
import { getUserDataForUserAnalytics } from "../../user-profile/user-profile.selectors";
import { PaymentDetails, PaymentType } from "../../checkout/shop-checkout.interface";
import { getShippingAddress, updatePaymentDetails } from "../../checkout/shop-checkout";
import { getCartAnalyticsOverrides } from '../../cart/shop-cart/shop-cart.analytics';

@customElement("mhecomm-venmo-button")
export default class VenmoButton extends connect(store)(BaseComponent) {
  static styles = [paymentStyles];
  /**
  * Braintree Client token to initialize Venmo button.
  */
  @property()
  clientToken = "";

  @property()
  profileId = "";

  /**
  * Transaction amount to be displayed in Venmo.
  */
  @property({ type: Number })
  amount = 0;

  /**
   * Currency for Venmo transaction.
   */
  @property()
  currency = "USD";

  clickEventMessage = "Venmo Checkout Start";

  stateChanged(state: StoreState) {
    if (getVenmoInstanceStatus(state) === ActionStatus.succeeded) {
      this.createVenmoButton();
    } 
  }
  /**
   * @param _changedProperties 
   */
  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    await this.setupVenmoButton();
  }

  async setupVenmoButton() {
    await store.dispatch(createVenmoInstance({
      clientToken: this.clientToken,
      profileId: this.profileId,
    }));
  }

  renderComp() {
    return html`
      <button class="btn btn-default p-0 pe-auto venmoButtonLogo" id="venmo-button" aria-label="Venmo Payment Method">
        <img src="/uxt/static/external/misc/logo_venmo_active.svg" alt="Venmo Payment Method" aria-label="Venmo Payment Method" >
         <img src="/uxt/static/external/misc/logo_venmo_inactive.svg" alt="Venmo Payment Method" aria-label="Venmo Payment Method" class="active"> </img>
      </button>
    `;
  }

  async createVenmoButton() {
    const results = getVenmoInstance(store.getState() as StoreState);
    var venmoInstance = results[1];
    await waitForElementsToLoad(this.shadowRoot!, [
      "#venmo-button",
    ]);

    if (!venmoInstance.isBrowserSupported()) {
      console.log('Browser does not support Venmo');
      return;
    }
    this.displayVenmoButton(venmoInstance);
  }

  private venmoButtonSelector() {
    return this.shadowRoot!.querySelector("#venmo-button") as HTMLElement;
  }

  private displayVenmoButton(venmoInstance) {

    this.venmoButtonSelector().classList.remove('d-none');

    this.venmoButtonSelector().addEventListener('click', () => {
      this.venmoButtonSelector().classList.add('disabled')

      const state = store.getState() as StoreState;
      emit({
        element: this,
        dataLayer: true,
        name: `mhecomm-venmo-checkout-start`,
        detail: {
          label: this.clickEventMessage,
          url: window.location.href,
          user: getUserDataForUserAnalytics(state),
          orderTotal: this.amount,
          ...getCartAnalyticsOverrides(state),
        }
      });

      venmoInstance.tokenize().then(this.handleVenmoSuccess).catch(this.handleVenmoError).then(() => {
        this.venmoButtonSelector().classList.remove('disabled')
      });
    })
  }

  private handleVenmoError(err) {
    if (err.code === 'VENMO_CANCELED') {
      console.log('App is not available or user aborted payment flow');
    } else if (err.code === 'VENMO_APP_CANCELED') {
      console.log('User canceled payment flow');
    } else {
      console.error('An error occurred:', err.message);
    }
  }

  private handleVenmoSuccess(payload) {

    const results = getVenmoInstance(store.getState() as StoreState);
    var dataCollectorInstance = results[0];
    const paymentDetails: PaymentDetails = {
      paymentType: PaymentType.BRAINTREE_VENMO,
      paymentNonce: payload.nonce,
      venmoUserName: payload.details.username,
      deviceData: dataCollectorInstance?.deviceData
    };

    emit({
      element: this,
      name: `mhecomm-venmo-checkout-info-collected`,
      dataLayer: true,
      detail: {
        paymentDetails: paymentDetails,
        shippingAddress: getShippingAddress(store.getState() as StoreState)
      },
      composed: true
    });
    store.dispatch(updatePaymentDetails(paymentDetails));

  }

}
declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-venmo-button': VenmoButton;
  }
}
